import React from "react";
import { Box, Divider, Popover, Theme, Typography } from '@mui/material'
import { makeStyles } from '@Compat/makeStyles';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

/**
 * The theme catalogue and its picker popover, without any app state: the
 * app's ThemePicker (Redux + CustomThemeContext) and the marketing site
 * (its own provider, no store) both render this with their own selection.
 */
export type ThemeMode = "light" | "dark";

export interface ThemeOption {
    name: string;
    label: string;
    /** Any CSS background — a colour or a gradient. */
    color: string;
    mode: ThemeMode;
}

export interface ThemeFamily {
    /** i18n key under the ThemePicker namespace */
    labelKey: string;
    options: ThemeOption[];
}

export const THEME_FAMILIES: ThemeFamily[] = [
    {
        labelKey: "familyClassic",
        options: [
            { name: "defaultLight",        label: "Fidava", color: "#2340B0", mode: "light" },
            { name: "defaultDark",         label: "Fidava", color: "#6F8BFF", mode: "dark" },
            { name: "materialBlueLigth",   label: "Blue",   color: "#1565c0", mode: "light" },
            { name: "materialBlueDark",    label: "Blue",   color: "#1976d2", mode: "dark" },
            { name: "materialLimeLigth",   label: "Lime",   color: "#558b2f", mode: "light" },
            { name: "materialLimeDark",    label: "Lime",   color: "#8bc34a", mode: "dark" },
            { name: "materialOrangeLigth", label: "Orange", color: "#e65100", mode: "light" },
            { name: "materialOrangeDark",  label: "Orange", color: "#ff9800", mode: "dark" },
            { name: "materialPurpleLigth", label: "Purple", color: "#6a1b9a", mode: "light" },
            { name: "materialPurpleDark",  label: "Purple", color: "#9c27b0", mode: "dark" },
        ],
    },
    {
        labelKey: "familyMetallic",
        options: [
            { name: "metallicSilver",       label: "Silver",    color: "#B0B7BF", mode: "light" },
            { name: "metallicGold",         label: "Gold",      color: "#D4AF37", mode: "light" },
            { name: "metallicRoseGold",     label: "Rose Gold", color: "#D8A0A6", mode: "light" },
            { name: "metallicGunmetalDark", label: "Gunmetal",  color: "#2C3539", mode: "dark" },
        ],
    },
    {
        labelKey: "familyNeutral",
        options: [
            { name: "neutralGraphiteDark", label: "Graphite", color: "#3A3C40", mode: "dark" },
            { name: "neutralSand",         label: "Sand",     color: "#D8C7AE", mode: "light" },
            { name: "neutralCharcoalDark", label: "Charcoal", color: "#2B2B2B", mode: "dark" },
        ],
    },
    {
        labelKey: "familyPastel",
        options: [
            { name: "pastelBlue",     label: "Pastel Blue",     color: "#A7C7E7", mode: "light" },
            { name: "pastelMint",     label: "Pastel Mint",     color: "#98D8C8", mode: "light" },
            { name: "pastelPeach",    label: "Pastel Peach",    color: "#FFCBA4", mode: "light" },
            { name: "pastelLavender", label: "Pastel Lavender", color: "#C3B1E1", mode: "light" },
        ],
    },
    {
        labelKey: "familyVibrant",
        options: [
            { name: "vibrantCrimsonDark", label: "Crimson",   color: "#DC143C", mode: "dark" },
            { name: "vibrantEmeraldDark", label: "Emerald",   color: "#50C878", mode: "dark" },
            { name: "vibrantCyanDark",    label: "Cyan Neon", color: "#00E5FF", mode: "dark" },
            { name: "vibrantMagentaDark", label: "Magenta",   color: "#FF3DAE", mode: "dark" },
        ],
    },
    {
        labelKey: "familyFuturistic",
        options: [
            { name: "futuristicNeonDark",      label: "Neon",      color: "linear-gradient(135deg, #00E5FF, #B44CFF)", mode: "dark" },
            { name: "futuristicSynthwaveDark", label: "Synthwave", color: "linear-gradient(135deg, #FF2E97, #FF9F1C)", mode: "dark" },
            { name: "futuristicMatrixDark",    label: "Matrix",    color: "linear-gradient(135deg, #00FF9C, #7CFF4F)", mode: "dark" },
            { name: "futuristicAuroraDark",    label: "Aurora",    color: "linear-gradient(135deg, #2DE2C4, #6C7BFF)", mode: "dark" },
            { name: "futuristicHologram",      label: "Hologram",  color: "linear-gradient(135deg, #0077C8, #7C3AED)", mode: "light" },
        ],
    },
];

// Fixed pixel paddings (the app's 4px spacing unit): the site theme uses
// MUI's 8px unit, and the panel should look the same in both.
const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        width: 230,
        maxHeight: 460,
        borderRadius: 12,
        overflowY: "auto",
        // Slim, unobtrusive scrollbar instead of the OS default fat one.
        // Firefox uses the standard properties; WebKit/Chromium/Edge need the
        // vendor pseudo-elements.
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.palette.action.disabled} transparent`,
        "&::-webkit-scrollbar": {
            width: 6,
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.text.secondary,
        },
    },
    header: {
        padding: "12px 16px",
        fontWeight: 700,
        fontSize: "0.8rem",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    sectionLabel: {
        padding: "8px 16px 2px",
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: theme.palette.text.secondary,
    },
    option: {
        display: "flex",
        alignItems: "center",
        padding: "6px 16px",
        cursor: "pointer",
        transition: "background 0.15s",
        userSelect: "none",
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    optionSelected: {
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.06)",
    },
    swatch: {
        width: 18,
        height: 18,
        borderRadius: "50%",
        flexShrink: 0,
        boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
    },
    optionLabel: {
        fontSize: "0.84rem",
        marginLeft: 12,
        flex: 1,
        color: theme.palette.text.primary,
    },
    modeTag: {
        fontSize: "0.62rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: theme.palette.text.secondary,
        opacity: 0.75,
        marginLeft: 4,
    },
    checkIcon: {
        color: theme.palette.text.secondary,
        marginLeft: 8,
    },
}));

interface IThemePickerPanelProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    /** Name of the selected theme (a key of getTheme). */
    value: string;
    onSelect: (name: string) => void;
}

export default function ThemePickerPanel({ anchorEl, onClose, value, onSelect }: IThemePickerPanelProps) {
    const classes = useStyles();
    const { t } = useTranslation("ThemePicker");

    const renderOption = (option: ThemeOption) => {
        const isSelected = value === option.name;
        const modeLabel = t(option.mode === "dark" ? "sectionDark" : "sectionLight");
        return (
            <Box
                key={option.name}
                className={`${classes.option} ${isSelected ? classes.optionSelected : ""}`}
                onClick={() => onSelect(option.name)}
            >
                <span className={classes.swatch} style={{ background: option.color }} />
                <Typography className={classes.optionLabel}>{option.label}</Typography>
                <Typography component="span" className={classes.modeTag}>{modeLabel}</Typography>
                {isSelected && (
                    <span className={classes.checkIcon}>
                        <FontAwesomeIcon icon={faCheck} size="xs" />
                    </span>
                )}
            </Box>
        );
    };

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            PaperProps={{ className: classes.paper }}
        >
            <Typography className={classes.header}>{t("header")}</Typography>

            {THEME_FAMILIES.map((family, index) => (
                <React.Fragment key={family.labelKey}>
                    {index > 0 && <Divider style={{ margin: "8px 0" }} />}
                    <Typography className={classes.sectionLabel}>{t(family.labelKey)}</Typography>
                    {family.options.map(renderOption)}
                </React.Fragment>
            ))}
        </Popover>
    );
}
